import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle2, User, FileText, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';
import api from '@/lib/api';

export default function ReportsPage() {
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);
  const [proctoringReports, setProctoringReports] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, candsRes, callsRes] = await Promise.all([
          api.get('/jobs'), api.get('/candidates'), api.get('/calls')
        ]);
        setJobs(jobsRes.data || []);
        setCandidates((candsRes.data || []).filter(c => c.score !== null || c.status === 'screened' || c.status === 'shortlisted'));
        setCalls((callsRes.data || []).filter(c => c.status === 'completed'));
      } catch (err) { logger.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const getJobTitle = (jobId) => jobs.find(j => j.job_id === jobId)?.title || 'Unknown';

  const loadProctoringReport = async (callId) => {
    if (proctoringReports[callId]) return;
    try {
      const { data } = await api.get(`/proctoring/report/${callId}`);
      setProctoringReports(prev => ({ ...prev, [callId]: data }));
    } catch (err) { logger.error(err); }
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-slate-400';
    if (score >= 85) return 'text-emerald-600';
    if (score >= 70) return 'text-[#2563eb]';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full bg-white"><div className="w-8 h-8 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="flex-1 overflow-auto bg-transparent p-6 sm:p-8 relative" data-testid="reports-page" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="bg-white/90 backdrop-blur-xl border border-slate-200/60 rounded-3xl shadow-xl p-6 mb-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-100/50 to-transparent rounded-full blur-3xl pointer-events-none -z-10 translate-x-1/4 -translate-y-1/4" />
        <h1 className="text-3xl font-black bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent tracking-tight">Reports</h1>
        <p className="text-slate-500 text-sm mt-1">AI scoring results and proctoring analysis</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Scored Candidates', value: candidates.length, color: 'text-slate-800' },
          { label: 'Shortlisted', value: candidates.filter(c => c.status === 'shortlisted').length, color: 'text-emerald-600' },
          { label: 'Completed Calls', value: calls.length, color: 'text-[#2563eb]' },
          { label: 'Avg Score', value: candidates.length > 0 ? (candidates.reduce((s, c) => s + (c.score || 0), 0) / candidates.length).toFixed(1) + '/10' : '—', color: 'text-amber-600' }
        ].map((stat) => (
          <div key={stat.label} className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{stat.label}</p>
            <p className={`text-4xl font-black mb-1 ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Candidate Reports */}
      {candidates.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Resume Scoring Reports</h2>
          <div className="space-y-3">
            {candidates.map(candidate => (
              <div key={candidate.candidate_id} className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <button
                  className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors text-left"
                  onClick={() => setExpandedReport(expandedReport === candidate.candidate_id ? null : candidate.candidate_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 border border-[#2563eb]/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#2563eb]" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-semibold">{candidate.name}</p>
                      <p className="text-xs text-slate-400">{getJobTitle(candidate.job_id)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {candidate.score !== null && (
                      <div className="text-right">
                        <p className={`text-xl font-bold ${getScoreColor(candidate.score_details?.overall_score || candidate.score * 10)}`}>
                          {candidate.score_details?.overall_score || candidate.score * 10}/100
                        </p>
                        <p className="text-xs text-slate-400">{candidate.score_details?.grade || 'Scored'}</p>
                      </div>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      candidate.status === 'shortlisted' ? 'bg-emerald-50 text-emerald-700' :
                      candidate.status === 'screened' ? 'bg-blue-50 text-[#2563eb]' :
                      'bg-slate-100 text-slate-500'
                    }`}>{candidate.status}</span>
                    {expandedReport === candidate.candidate_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {expandedReport === candidate.candidate_id && candidate.score_details && (
                  <div className="px-5 pb-5 border-t border-slate-100">
                    <div className="pt-4 grid grid-cols-2 gap-6">
                      {candidate.score_details.criteria_scores && (
                        <div>
                          <p className="text-xs text-slate-400 mb-3 uppercase tracking-wider">Criteria Scores</p>
                          <div className="space-y-2.5">
                            {Object.entries(candidate.score_details.criteria_scores).map(([key, val]) => (
                              <div key={key}>
                                <div className="flex justify-between mb-1">
                                  <span className="text-xs text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                  <span className="text-xs font-semibold text-slate-800">{val.score}%</span>
                                </div>
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#2563eb] rounded-full" style={{ width: `${val.score}%` }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        {candidate.score_details.detailed_summary?.strengths?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs text-emerald-600 mb-2 uppercase tracking-wider">Strengths</p>
                            <ul className="space-y-1">
                              {candidate.score_details.detailed_summary.strengths.slice(0, 3).map((s) => (
                                <li key={s} className="text-xs text-slate-600 flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {candidate.score_details.detailed_summary?.concerns?.length > 0 && (
                          <div>
                            <p className="text-xs text-red-500 mb-2 uppercase tracking-wider">Concerns</p>
                            <ul className="space-y-1">
                              {candidate.score_details.detailed_summary.concerns.slice(0, 3).map((c) => (
                                <li key={c} className="text-xs text-slate-600 flex gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />{c}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    {candidate.score_details.recommendation && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <p className="text-xs text-slate-400 mb-0.5">AI Recommendation</p>
                        <p className="text-sm text-slate-700">{candidate.score_details.recommendation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Calls with Proctoring */}
      {calls.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Screening Session Reports</h2>
          <div className="space-y-3">
            {calls.map(call => (
              <div key={call.call_id} className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <button
                  className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors text-left"
                  onClick={() => {
                    setExpandedReport(expandedReport === call.call_id ? null : call.call_id);
                    if (expandedReport !== call.call_id) loadProctoringReport(call.call_id);
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-[#2563eb]" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-semibold">{call.candidate_name || 'Candidate'}</p>
                      <p className="text-xs text-slate-400">{call.job_title || 'Screening'} &middot; {call.duration_seconds ? Math.round(call.duration_seconds / 60) + ' min' : 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-slate-500">Proctoring: <span className={`font-bold ${
                      call.proctoring_score >= 80 ? 'text-emerald-600' :
                      call.proctoring_score >= 50 ? 'text-amber-600' : 'text-red-500'
                    }`}>{call.proctoring_score || 100}%</span></p>
                    {expandedReport === call.call_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {expandedReport === call.call_id && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                    {proctoringReports[call.call_id] ? (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                          <p className="text-xs text-emerald-600 mb-1">Integrity Score</p>
                          <p className="text-2xl font-bold text-emerald-700">{proctoringReports[call.call_id].score?.overall_integrity_score?.toFixed(0)}%</p>
                        </div>
                        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                          <p className="text-xs text-amber-600 mb-1">AI Likelihood</p>
                          <p className="text-2xl font-bold text-amber-700">{proctoringReports[call.call_id].score?.ai_assistance_likelihood?.toFixed(0)}%</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                          <p className="text-xs text-[#2563eb] mb-1">Recommendation</p>
                          <p className="text-lg font-bold text-[#2563eb] capitalize">{proctoringReports[call.call_id].recommendation}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <div className="w-4 h-4 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
                        Generating report...
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {candidates.length === 0 && calls.length === 0 && (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-2xl">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No reports yet</p>
          <p className="text-slate-400 text-sm mt-1">Score candidates to generate reports</p>
        </div>
      )}
    </div>
  );
}
